/**
 * InstallApp — final CTA with PWA install instructions (Android / iPhone).
 * The app is installable (manifest + service worker); no app store needed.
 */
import React from 'react';
import { motion } from 'framer-motion';

const STEPS = {
  android: [
    'Abre la app en Chrome',
    'Toca el menú ⋮ (arriba a la derecha)',
    'Elige «Instalar aplicación»',
  ],
  iphone: [
    'Abre la app en Safari',
    'Toca el botón Compartir',
    'Elige «Agregar a pantalla de inicio»',
  ],
};

const InstallApp = () => (
  <section
    id="instalar"
    className="cdx-grain"
    style={{ padding: '88px 0', background: 'var(--cdx-paper-2)', fontFamily: 'var(--cdx-font-body)' }}
  >
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <span className="cdx-kicker">Llévala contigo</span>
        <h2 className="cdx-display" style={{ fontSize: 'clamp(2rem, 4.5vw, 3rem)', margin: '10px 0 12px', color: 'var(--cdx-ink)' }}>
          Instálala como app, <em style={{ color: 'var(--cdx-jade)', fontStyle: 'italic' }}>sin tiendas</em>
        </h2>
        <p style={{ fontSize: 17, color: 'var(--cdx-ink-soft)', maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
          Se abre a pantalla completa, con su propio ícono, y carga al instante.
          Dos pasos desde tu navegador:
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        {[
          { key: 'android', name: 'Android · Chrome', icon: <AndroidIcon /> },
          { key: 'iphone', name: 'iPhone · Safari', icon: <AppleIcon /> },
        ].map(({ key, name, icon }, idx) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5, delay: idx * 0.1 }}
            style={{
              background: 'var(--cdx-paper)',
              border: '1px solid var(--cdx-paper-edge)',
              borderRadius: 18,
              boxShadow: 'var(--cdx-shadow-sm)',
              padding: '26px 28px',
            }}
          >
            <div className="flex items-center gap-3 mb-5">
              {icon}
              <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--cdx-ink)' }}>{name}</h3>
            </div>
            <ol style={{ display: 'grid', gap: 12, margin: 0, padding: 0, listStyle: 'none' }}>
              {STEPS[key].map((step, i) => (
                <li key={step} className="flex items-center gap-3">
                  <span
                    style={{
                      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                      background: 'var(--cdx-jade)', color: 'var(--cdx-nighttext, #EBE2CF)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'var(--cdx-font-mono)', fontSize: 13, fontWeight: 600,
                    }}
                  >
                    {i + 1}
                  </span>
                  <span style={{ fontSize: 15.5, color: 'var(--cdx-ink-soft)' }}>{step}</span>
                </li>
              ))}
            </ol>
          </motion.div>
        ))}
      </div>

      <div className="text-center">
        <a href="/dashboard" className="cdx-btn cdx-btn--primary" style={{ padding: '15px 30px', fontSize: 17, display: 'inline-flex' }}>
          Abrir la app ahora
        </a>
      </div>
    </div>
  </section>
);

const AndroidIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="var(--cdx-jade)" strokeWidth="1.8" aria-hidden>
    <rect x="6" y="3" width="12" height="18" rx="2.5" />
    <line x1="10" y1="18.5" x2="14" y2="18.5" strokeLinecap="round" />
  </svg>
);

const AppleIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="var(--cdx-jade)" strokeWidth="1.8" aria-hidden>
    <rect x="6" y="3" width="12" height="18" rx="2.5" />
    <path d="M12 8v6M9 11l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default InstallApp;
